<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import BarChart from 'shared/components/charts/BarChart.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({ from: null, to: null })
  }
});

const isLoading = ref(true);
const metricsData = ref(null);

const fetchMetrics = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (props.filters.from && props.filters.to) {
      params.from = props.filters.from;
      params.to = props.filters.to;
    }
    const data = await store.dispatch('campaigns/fetchDashboardMetrics', params);
    metricsData.value = data;
  } catch (e) {
    console.error('Error fetching dashboard metrics', e);
  }
  isLoading.value = false;
};

let pollingInterval = null;

onMounted(() => {
  fetchMetrics();
  
  pollingInterval = setInterval(() => {
    fetchMetrics();
  }, 10000); // 10 seconds
});

onUnmounted(() => {
  if (pollingInterval) clearInterval(pollingInterval);
});

watch(() => props.filters, () => {
  fetchMetrics();
}, { deep: true });

const downloadSubscriptionsReport = async () => {
  try {
    const response = await window.axios.get('/api/v1/accounts/' + store.getters['getCurrentAccountId'] + '/campaigns/export_subscriptions_report', {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contactos_desuscritos_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (error) {
    console.error('Error downloading CSV', error);
  }
};

const statusChartData = computed(() => {
  if (!metricsData.value) return { labels: [], datasets: [] };
  
  const statusCounts = metricsData.value.status_counts || {};
  return {
    labels: ['Completadas', 'En Progreso', 'Fallidas', 'Pausadas'],
    datasets: [
      {
        label: 'Estado de Campañas',
        data: [
          statusCounts.completed || 0,
          statusCounts.in_progress || 0,
          statusCounts.failed || 0,
          statusCounts.paused || 0
        ],
        backgroundColor: ['#10b981', '#3b82f6', '#ef4444', '#f59e0b'],
      }
    ]
  };
});

const deliveryChartData = computed(() => {
  if (!metricsData.value) return { labels: [], datasets: [] };
  
  const delivery = metricsData.value.delivery_metrics || {};
  return {
    labels: ['Entregados', 'Leídos', 'Fallidos', 'Enviados', 'Procesando'],
    datasets: [
      {
        label: 'Tasa de Entrega',
        data: [
          delivery.delivered || 0,
          delivery.read || 0,
          delivery.failed || 0,
          delivery.sent || 0,
          delivery.processing || 0
        ],
        backgroundColor: ['#10b981', '#8b5cf6', '#ef4444', '#3b82f6', '#f59e0b'],
      }
    ]
  };
});

const statusChartOptions = {
  plugins: { legend: { display: false } }
};

</script>

<template>
  <div>
    <div class="mb-6 p-4 bg-n-blue-2 dark:bg-n-blue-3/10 border border-n-blue-4 rounded-xl flex items-start gap-3 shadow-sm">
      <span class="i-lucide-info text-n-blue-9 w-5 h-5 flex-shrink-0 mt-0.5"></span>
      <div>
        <h4 class="text-sm font-semibold text-n-blue-11">Motor de Cumplimiento Normativo Automático</h4>
        <p class="text-sm text-n-blue-11/80 mt-1">
          Las campañas están sujetas a la normativa legal del país destino (ej. Ley 2300). El sistema pausará de forma automática los envíos fuera de los horarios comerciales permitidos o en días festivos, y omitirá inteligentemente a los usuarios que se hayan desuscrito.
        </p>
      </div>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <!-- Chart 1: Status -->
    <div class="bg-white dark:bg-n-slate-2 border border-n-slate-3 p-4 rounded-xl shadow-sm">
      <h3 class="text-sm font-semibold text-n-slate-11 mb-4 uppercase tracking-wider">Estado de Campañas</h3>
      <div v-if="isLoading" class="flex justify-center items-center h-48">
        <span class="i-lucide-loader-2 animate-spin w-6 h-6 text-n-slate-11"></span>
      </div>
      <div v-else class="h-48">
        <BarChart :collection="statusChartData" :chart-options="statusChartOptions" />
      </div>
    </div>
    
    <!-- Chart 2: Delivery -->
    <div class="bg-white dark:bg-n-slate-2 border border-n-slate-3 p-4 rounded-xl shadow-sm">
      <h3 class="text-sm font-semibold text-n-slate-11 mb-4 uppercase tracking-wider">Métricas de Envío Globales</h3>
      <div v-if="isLoading" class="flex justify-center items-center h-48">
        <span class="i-lucide-loader-2 animate-spin w-6 h-6 text-n-slate-11"></span>
      </div>
      <div v-else class="h-48">
        <BarChart :collection="deliveryChartData" :chart-options="statusChartOptions" />
      </div>
    </div>
    
    <!-- Box 3: Subscriptions -->
    <div class="bg-white dark:bg-n-slate-2 border border-n-slate-3 p-4 rounded-xl shadow-sm flex flex-col justify-center items-center">
      <h3 class="text-sm font-semibold text-n-slate-11 mb-2 uppercase tracking-wider text-center">Contactos Desuscritos</h3>
      <div v-if="isLoading" class="flex justify-center items-center h-24">
        <span class="i-lucide-loader-2 animate-spin w-6 h-6 text-n-slate-11"></span>
      </div>
      <div v-else class="flex flex-col items-center">
        <div class="text-4xl font-bold text-n-slate-12 mb-2">
          {{ metricsData?.opt_out_count || 0 }}
        </div>
        <Button variant="outline" size="sm" @click="downloadSubscriptionsReport" icon="i-lucide-download">
          Descargar Informe (CSV)
        </Button>
      </div>
      <p class="text-xs text-n-slate-10 mt-3 text-center">Informe de altas y bajas de WhatsApp</p>
    </div>
  </div>
  </div>
</template>
